import * as React from "react";
import { Badge } from "@mindstonehq/ui";
import { classNames } from "~/utils";

export interface IReactMultiEmailProps {
  emails?: string[];
  onChange?: (emails: string[]) => void;
  noClass?: boolean;
  validateEmail?: (email: string) => boolean;
  style?: React.CSSProperties;
  autofocus?: boolean;
  className?: string;
  placeholder?: string | React.ReactNode;
}

export interface IReactMultiEmailState {
  focused?: boolean;
  propsEmails?: string[];
  emails: string[];
  inputValue?: string;
}

export class EmailsInput extends React.Component<IReactMultiEmailProps, IReactMultiEmailState> {
  state = {
    focused: false,
    emails: [],
    inputValue: "",
  };

  emailInputRef = React.createRef<HTMLInputElement>();

  static getDerivedStateFromProps(nextProps: IReactMultiEmailProps, prevState: IReactMultiEmailState) {
    if (prevState.propsEmails !== nextProps.emails) {
      return {
        propsEmails: nextProps.emails || [],
        emails: nextProps.emails || [],
        inputValue: "",
        focused: false,
      };
    }
    return null;
  }

  constructor(props: IReactMultiEmailProps) {
    super(props);
  }

  findEmailAddress = (value: string, isEnter?: boolean) => {
    const { validateEmail } = this.props;
    let validEmails: string[] = [];
    let inputValue: string = "";
    const re = /[ ,;]/g;
    const isEmailFn = validateEmail || isEmail;

    const addEmails = (email: string) => {
      const emails: string[] = this.state.emails;
      for (let i = 0, l = emails.length; i < l; i++) {
        if (emails[i] === email) {
          return false;
        }
      }
      validEmails.push(email);
      return true;
    };

    if (value !== "") {
      if (re.test(value)) {
        let splitData = value.split(re).filter((n) => {
          return n !== "" && n !== undefined && n !== null;
        });

        const setArr = new Set(splitData);
        let arr = [...setArr];

        do {
          if (isEmailFn("" + arr[0])) {
            addEmails("" + arr.shift());
          } else {
            if (arr.length === 1) {
              inputValue = "" + arr.shift();
            } else {
              arr.shift();
            }
          }
        } while (arr.length);
      } else {
        if (isEnter) {
          if (isEmailFn(value)) {
            addEmails(value);
          } else {
            inputValue = value;
          }
        } else {
          inputValue = value;
        }
      }
    }

    this.setState({
      emails: [...this.state.emails, ...validEmails],
      inputValue: inputValue,
    });

    if (validEmails.length && this.props.onChange) {
      this.props.onChange([...this.state.emails, ...validEmails]);
    }
  };

  onChangeInputValue = (value: string) => {
    this.findEmailAddress(value);
  };

  removeEmail = (index: number, isDisabled?: boolean) => {
    if (isDisabled) {
      return;
    }
    this.setState(
      (prevState) => {
        return {
          emails: [...prevState.emails.slice(0, index), ...prevState.emails.slice(index + 1)],
        };
      },
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.state.emails);
        }
      },
    );
  };

  handleOnKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.which) {
      case 13:
      case 9:
        e.preventDefault();
        break;
      case 8:
        if (!e.currentTarget.value) {
          this.removeEmail(this.state.emails.length - 1, false);
        }
        break;
      default:
    }
  };

  handleOnKeyup = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.which) {
      case 13:
      case 9:
        this.findEmailAddress(e.currentTarget.value, true);
        break;
      default:
    }
  };

  handleOnChange = (e: React.SyntheticEvent<HTMLInputElement>) => this.onChangeInputValue(e.currentTarget.value);

  handleOnBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState({ focused: false });
    this.findEmailAddress(e.currentTarget.value, true);
  };

  handleOnFocus = () =>
    this.setState({
      focused: true,
    });

  render() {
    const { focused, emails, inputValue } = this.state;
    const { style, className = "", noClass, placeholder, autofocus } = this.props;

    return (
      <div
        className={classNames(
          "flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-placeholder-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          "w-full h-auto  min-h-12 relative flex flex-1 flex-wrap block p-2 pt-3 pl-0.5 input-md [&_.placeholder]:top-3",
        )}
        style={style}
        onClick={() => {
          if (this.emailInputRef.current) {
            this.emailInputRef.current.focus();
          }
        }}
      >
        {placeholder ? (
          <span
            className={classNames(
              "placeholder inline text-md font-medium text-placeholder-foreground absolute pt-[1px] top-2 px-3",
              emails?.length || inputValue ? "hidden" : "",
            )}
          >
            {placeholder}
          </span>
        ) : null}
        {emails.map((email: string, index: number) => (
          <Badge
            className={
              "max-w-full text-sm font-medium mb-0.5 bg-muted ml-2 h-6 px-1 shrink-0 rounded-md overflow-hidden flex justify-center items-center"
            }
            variant="secondary"
            key={index}
          >
            {email}
            <span className={"ml-2 cursor-pointer"} onClick={() => this.removeEmail(index)}>
              ×
            </span>
          </Badge>
        ))}
        <input
          id="emails-input"
          type={"url"}
          ref={this.emailInputRef}
          className={
            "pl-3 bg-muted input outline-0 text-sm font-medium border-none !shadow-none focus:shadow-none h-6 focus:outline-0 focus:ring-0 focus:border-none hover:shadow-none inline-block grow rounded-md"
          }
          style={{
            border: "none!important",
            boxShadow: "none!important",
          }}
          value={inputValue}
          autoFocus={autofocus}
          onFocus={this.handleOnFocus}
          onBlur={this.handleOnBlur}
          onChange={this.handleOnChange}
          onKeyDown={this.handleOnKeydown}
          onKeyUp={this.handleOnKeyup}
        />
      </div>
    );
  }
}

export interface IFqdnOptions {
  requireTld?: boolean;
  allowUnderscores?: boolean;
  allowTrailingDot?: boolean;
}

export interface IEmailOptions {
  allowDisplayName?: boolean;
  requireDisplayName?: boolean;
  allowUtf8LocalPart?: boolean;
  requireTld?: boolean;
}

const defaultFqdnOptions = {
  requireTld: true,
  allowUnderscores: false,
  allowTrailingDot: false,
};

const defaultEmailOptions = {
  allowDisplayName: false,
  requireDisplayName: false,
  allowUtf8LocalPart: true,
  requireTld: true,
};

const displayName =
  /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\.\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\,\.\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF\s]*<(.+)>$/i;
const emailUserPart = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~]+$/i;
const quotedEmailUser =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f]))*$/i;
const emailUserUtf8Part = /^[a-z\d!#\$%&'\*\+\-\/=\?\^_`{\|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+$/i;
const quotedEmailUserUtf8 =
  /^([\s\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|(\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*$/i;

function isByteLength(str: string, options: { min?: number; max?: number }) {
  let min: number = 0;
  let max: number | undefined;
  const len = encodeURI(str).split(/%..|./).length - 1;

  if (options) {
    min = Number(options.min) || 0;
    max = Number(options.max);
  }

  return len >= min && (typeof max === "undefined" || len <= max);
}

function isFQDN(str: string, options?: IFqdnOptions) {
  options = { ...defaultFqdnOptions, ...options };

  /* Remove the optional trailing dot before checking validity */
  if (options.allowTrailingDot && str[str.length - 1] === ".") {
    str = str.substring(0, str.length - 1);
  }
  const parts = str.split(".");

  if (options.requireTld) {
    const tld: string = "" + parts.pop();
    if (!parts.length || !/^([a-z\u00a1-\uffff]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) {
      return false;
    }
    // disallow spaces
    if (/[\s\u2002-\u200B\u202F\u205F\u3000\uFEFF\uDB40\uDC20]/.test(tld)) {
      return false;
    }
  }

  for (let part, i = 0; i < parts.length; i++) {
    part = parts[i];
    if (options.allowUnderscores) {
      part = part.replace(/_/g, "");
    }
    if (!/^[a-z\u00a1-\uffff0-9-]+$/i.test(part)) {
      return false;
    }
    // disallow full-width chars
    if (/[\uff01-\uff5e]/.test(part)) {
      return false;
    }
    if (part[0] === "-" || part[part.length - 1] === "-") {
      return false;
    }
  }
  return true;
}

export function isEmail(str: string, options?: IEmailOptions) {
  options = { ...defaultEmailOptions, ...options };

  if (options.requireDisplayName || options.allowDisplayName) {
    const displayEmail = str.match(displayName);
    if (displayEmail) {
      str = displayEmail[1];
    } else if (options.requireDisplayName) {
      return false;
    }
  }

  const parts: string[] = str.split("@");
  const domain: string = "" + parts.pop();
  const lowerDomain = domain.toLowerCase();
  let user: string = parts.join("@");

  if (lowerDomain === "gmail.com" || lowerDomain === "googlemail.com") {
    user = user.replace(/\./g, "").toLowerCase();
  }

  if (!isByteLength(user, { max: 64 }) || !isByteLength(domain, { max: 254 })) {
    return false;
  }

  if (!isFQDN(domain, { requireTld: options.requireTld })) {
    return false;
  }

  if (user[0] === '"') {
    user = user.slice(1, user.length - 1);
    return options.allowUtf8LocalPart ? quotedEmailUserUtf8.test(user) : quotedEmailUser.test(user);
  }

  const pattern = options.allowUtf8LocalPart ? emailUserUtf8Part : emailUserPart;
  const userParts = user.split(".");

  for (let i = 0; i < userParts.length; i++) {
    if (!pattern.test(userParts[i])) {
      return false;
    }
  }

  return true;
}
